/* PURE STUDIOS — studio entrance, hanger, sketch reveal, photo viewer, archive layout. */
(() => {
  'use strict';

  const PS = window.PureStudios || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = matchMedia('(hover: hover) and (pointer: fine)');
  const html = document.documentElement;
  const store = {
    get(key) { try { return sessionStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch {} },
  };

  /* ------------------------------------------------------------ studio entrance */
  function initEntrance(entrance) {
    if (entrance.dataset.bound) return;
    entrance.dataset.bound = 'true';
    const intro = $('[data-intro]', entrance);
    const skip = $('[data-intro-skip]', entrance);
    const viewport = $('[data-studio-viewport]', entrance);
    const hint = $('[data-studio-hint]', entrance);
    const hanger = $('[data-hanger]', entrance);
    let holdTimer, safetyTimer;

    const setInert = (on) => {
      $$('.studio-nav, [data-studio-viewport], .studio__replay', entrance).forEach((el) => { el.inert = on; });
    };

    function finish() {
      clearTimeout(holdTimer); clearTimeout(safetyTimer);
      intro.classList.remove('is-wiping', 'is-playing');
      html.classList.remove('intro-pending');
      setInert(false);
      store.set('pure-intro-seen', '1');
      if (entrance.dataset.introDone !== 'true') {
        entrance.dataset.introDone = 'true';
        entrance.dispatchEvent(new CustomEvent('studio:ready', { bubbles: true }));
      }
    }

    function play() {
      if (reducedMotion.matches) { finish(); return; }
      entrance.dataset.introDone = 'false';
      hanger?.dispatchEvent(new CustomEvent('hanger:rewind'));
      intro.classList.remove('is-wiping');
      intro.classList.add('is-playing');
      void intro.offsetWidth;
      setInert(true);
      skip.focus({ preventScroll: true });
      holdTimer = setTimeout(() => intro.classList.add('is-wiping'), 1100);
      safetyTimer = setTimeout(finish, 3400);
    }

    $('.intro__ink', intro).addEventListener('animationend', (e) => { if (e.animationName === 'intro-wipe') finish(); });
    skip.addEventListener('click', finish);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && intro.classList.contains('is-playing')) finish(); });
    $('[data-intro-replay]', entrance).addEventListener('click', play);

    if (html.classList.contains('intro-pending')) play();
    else finish();

    // Phones: start the panorama on the hanger and the shirt; hide the hint after the first look around.
    if (viewport) {
      const center = () => {
        if (viewport.scrollWidth > viewport.clientWidth + 4) viewport.scrollLeft = (viewport.scrollWidth - viewport.clientWidth) * 0.55;
      };
      center();
      window.addEventListener('load', center, { once: true });
      let startX = null;
      viewport.addEventListener('scroll', () => {
        if (startX === null) startX = viewport.scrollLeft;
        if (Math.abs(viewport.scrollLeft - startX) > 24) hint?.classList.add('is-hidden');
      }, { passive: true });
    }

    // The 3D hanger loads after first paint; the flat mark stays if WebGL is unavailable.
    if (hanger && PS.hangerModule) {
      const load = () => import(PS.hangerModule)
        .then((m) => { if (hanger.isConnected) entrance._destroyHanger = m.mountHanger(hanger); })
        .catch(() => {});
      if ('requestIdleCallback' in window) requestIdleCallback(load, { timeout: 1200 }); else setTimeout(load, 200);
    }
  }

  /* ------------------------------------------------------------ sketch reveal */
  let revealId = 0;
  const inView = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const el = entry.target;
      if (!entry.isIntersecting) { clearTimeout(el._viewTimer); return; }
      el._viewTimer = setTimeout(() => { el._reveal?.('photo'); inView.unobserve(el); }, el.dataset.mode === 'detail' ? 650 : 280);
    });
  }, { threshold: 0.6 });

  function initReveal(el) {
    if (el.dataset.bound) return;
    el.dataset.bound = 'true';
    if (el.dataset.state !== 'sketch') return;

    const photo = $('.reveal__photo', el);
    const develop = $('.reveal__develop image', el);
    const clip = $('[data-reveal-clip]', el);
    const switches = $$('[data-reveal-show]', el);

    // Unique clip id per instance, and a second, offset graphite pass for each line.
    if (clip && develop) {
      const id = `reveal-clip-${++revealId}`;
      clip.id = id;
      develop.setAttribute('clip-path', `url(#${id})`);
    }
    $$('.reveal__stroke', el).forEach((path) => {
      const ghost = path.cloneNode();
      ghost.classList.add('reveal__stroke--ghost');
      path.after(ghost);
    });

    const ready = () => new Promise((resolve) => {
      if (!photo || (photo.complete && photo.naturalWidth)) return resolve();
      photo.addEventListener('load', resolve, { once: true });
      photo.addEventListener('error', resolve, { once: true });
    }).then(() => photo?.decode?.().catch(() => {}));

    let wanted = 'sketch';
    async function show(state) {
      wanted = state;
      if (state === 'photo') {
        if (develop && !develop.getAttribute('href')) develop.setAttribute('href', develop.dataset.href);
        if (photo && photo.loading === 'lazy') photo.loading = 'eager';
        await ready();
      }
      if (wanted !== state) return;
      el.dataset.state = state;
      switches.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.revealShow === state)));
    }
    el._reveal = show;

    if (el.dataset.mode === 'detail') {
      switches.forEach((b) => b.addEventListener('click', () => { inView.unobserve(el); show(b.dataset.revealShow); }));
      inView.observe(el);
      return;
    }

    // Grid cards: hover or keyboard focus reveals; leaving rewinds. Touch screens reveal as the card scrolls into view.
    const card = el.closest('.card__link') || el;
    if (canHover.matches) {
      card.addEventListener('pointerenter', () => show('photo'));
      card.addEventListener('pointerleave', () => show('sketch'));
      card.addEventListener('focusin', () => show('photo'));
      card.addEventListener('focusout', () => show('sketch'));
      // Warm the photo up when the pointer gets close, so the reveal never waits on the network.
      card.addEventListener('pointerenter', () => { if (photo) photo.loading = 'eager'; }, { once: true });
    } else {
      inView.observe(el);
    }
  }

  /* ------------------------------------------------------------ photo viewer */
  let viewer, group = [], index = 0;
  function buildViewer() {
    viewer = document.createElement('dialog');
    viewer.className = 'photo-viewer';
    viewer.setAttribute('aria-label', 'Photograph viewer');
    viewer.innerHTML = `
      <div class="photo-viewer__stage"><img alt="" decoding="async"></div>
      <div class="photo-viewer__bar"><span class="photo-viewer__caption"></span><span class="photo-viewer__count" aria-live="polite"></span></div>
      <button class="icon-button photo-viewer__close" type="button" aria-label="Close">
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>
      <button class="icon-button photo-viewer__prev" type="button" aria-label="Previous photograph">
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
      <button class="icon-button photo-viewer__next" type="button" aria-label="Next photograph">
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`;
    document.body.append(viewer);
    const img = $('img', viewer);
    $('.photo-viewer__close', viewer).addEventListener('click', () => viewer.close());
    $('.photo-viewer__prev', viewer).addEventListener('click', () => go(-1));
    $('.photo-viewer__next', viewer).addEventListener('click', () => go(1));
    viewer.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    });
    // Click on the dark surround closes; swipe left/right changes photo.
    const stage = $('.photo-viewer__stage', viewer);
    stage.addEventListener('click', (e) => { if (e.target === stage) viewer.close(); });
    let sx = null, sy = null;
    stage.addEventListener('pointerdown', (e) => { sx = e.clientX; sy = e.clientY; });
    stage.addEventListener('pointerup', (e) => {
      if (sx === null) return;
      const dx = e.clientX - sx, dy = e.clientY - sy;
      sx = null;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) go(dx < 0 ? 1 : -1);
    });
    img.addEventListener('load', () => img.classList.remove('is-loading'));
    viewer.addEventListener('close', () => { group[index]?.focus({ preventScroll: true }); });
  }
  const fullSrc = (button) => button.dataset.full || $('img', button)?.currentSrc || $('img', button)?.src;
  function show() {
    const button = group[index];
    const thumb = $('img', button);
    const img = $('img', viewer);
    const src = fullSrc(button);
    if (img.getAttribute('src') !== src) {
      img.classList.add('is-loading');
      img.src = src;
    }
    img.alt = thumb?.alt || '';
    $('.photo-viewer__caption', viewer).textContent = thumb?.alt || '';
    $('.photo-viewer__count', viewer).textContent = group.length > 1 ? `${index + 1} / ${group.length}` : '';
    $$('.photo-viewer__prev, .photo-viewer__next', viewer).forEach((b) => { b.hidden = group.length < 2; });
    // Preload neighbours so arrowing through feels instant.
    [1, -1].forEach((d) => { const n = group[(index + d + group.length) % group.length]; if (n) new Image().src = fullSrc(n); });
  }
  function go(step) { index = (index + step + group.length) % group.length; show(); }
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-photo-open]');
    if (!button) return;
    const container = button.closest('[data-photo-group]') || document;
    group = $$('[data-photo-open]', container);
    index = Math.max(0, group.indexOf(button));
    if (!viewer) buildViewer();
    show();
    viewer.showModal();
  });

  /* ------------------------------------------------------------ archive masonry */
  function initMasonry(grid) {
    if (grid.dataset.bound) return;
    grid.dataset.bound = 'true';
    const ROW = 4;
    const layout = () => {
      grid.classList.add('is-masonry');
      $$('.archive__item', grid).forEach((item) => {
        const img = $('img', item);
        if (!img) return;
        const h = img.getBoundingClientRect().height + parseFloat(getComputedStyle(item).paddingBottom || 0);
        item.style.gridRowEnd = `span ${Math.max(1, Math.ceil(h / ROW))}`;
      });
    };
    new ResizeObserver(() => requestAnimationFrame(layout)).observe(grid);
    layout();
  }

  /* ------------------------------------------------------------ boot */
  function init(root = document) {
    $$('[data-studio-entrance]', root).forEach(initEntrance);
    $$('[data-reveal]', root).forEach(initReveal);
    $$('[data-masonry]', root).forEach(initMasonry);
  }
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    $$('[data-studio-entrance]', e.target).forEach((el) => el._destroyHanger?.());
  });
})();
